import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

const formSchema = z.object({
  first_name: z.string().min(2, "İsim en az 2 karakter olmalıdır"),
  last_name: z.string().min(2, "Soyisim en az 2 karakter olmalıdır"),
  current_grade: z.string().min(1, "Sınıf seçimi zorunludur"),
  study_field: z.enum(["MF", "TM", "SÖZEL", "DİL"], {
    required_error: "Alan seçimi zorunludur",
  }),
  university: z.string().min(2, "Üniversite adı zorunludur"),
  major: z.string().min(2, "Bölüm adı zorunludur"),
  high_school: z.string().min(2, "Lise adı zorunludur"),
  exam_years: z.array(z.number()).min(1, "En az bir sınav yılı seçmelisiniz"),
  exam_ranks: z.array(z.string()).min(1, "Sınav derecenizi girmelisiniz"),
  email: z.string().email("Geçerli bir email adresi giriniz"),
  phone: z.string().min(10, "Geçerli bir telefon numarası giriniz"),
});

const examYears = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];

export const BecomeMentor = () => {
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      exam_years: [],
      exam_ranks: [],
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const { error } = await supabase.from("mentor_applications").insert({
        first_name: values.first_name,
        last_name: values.last_name,
        current_grade: values.current_grade,
        study_field: values.study_field,
        university: values.university,
        major: values.major,
        high_school: values.high_school,
        exam_years: values.exam_years,
        exam_ranks: values.exam_ranks,
        email: values.email,
        phone: values.phone,
        status: 'pending'
      });

      if (error) throw error;

      toast({
        title: "Başvurunuz alındı!",
        description: "En kısa sürede sizinle iletişime geçeceğiz.",
      });

      navigate("/");
    } catch (error) {
      console.error("Error submitting application:", error);
      toast({
        title: "Bir hata oluştu",
        description: "Lütfen daha sonra tekrar deneyiniz.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto py-24 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Mentör Başvuru Formu</h1>
          <p className="text-muted-foreground">
            Öğrencilere yardımcı olmak için ilk adımı atın
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>İsim</FormLabel>
                    <FormControl>
                      <Input placeholder="İsminizi giriniz" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Soyisim</FormLabel>
                    <FormControl>
                      <Input placeholder="Soyisminizi giriniz" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="current_grade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kaçıncı Sınıfsınız?</FormLabel>
                  <FormControl>
                    <Input placeholder="Örn: 3. Sınıf" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="study_field"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Alanınız</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="grid grid-cols-2 gap-4 sm:grid-cols-4"
                    >
                      {["MF", "TM", "SÖZEL", "DİL"].map((value) => (
                        <FormItem
                          key={value}
                          className="flex items-center space-x-2"
                        >
                          <FormControl>
                            <RadioGroupItem value={value} />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">
                            {value}
                          </FormLabel>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="university"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Üniversite</FormLabel>
                    <FormControl>
                      <Input placeholder="Üniversitenizi giriniz" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="major"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bölüm</FormLabel>
                    <FormControl>
                      <Input placeholder="Bölümünüzü giriniz" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="high_school"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mezun Olduğunuz Lise</FormLabel>
                  <FormControl>
                    <Input placeholder="Lisenizi giriniz" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="exam_years"
              render={() => (
                <FormItem>
                  <FormLabel>
                    Kaç yılında sınava girdiniz? (Mezuna kaldıysanız hem mezun
                    senenizi hem de ilk senenizi işaretleyiniz.)
                  </FormLabel>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {examYears.map((year) => (
                      <FormField
                        key={year}
                        control={form.control}
                        name="exam_years"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={year}
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(year)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, year])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== year
                                          )
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal cursor-pointer">
                                {year}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="exam_ranks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    YKS dereceniz nedir? (Mezuna kaldıysanız iki senenizin de
                    derecesini yazınız.)
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Örn: 5000, 2500"
                      value={field.value.join(", ")}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value.split(",").map((s) => s.trim())
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-posta</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="E-posta adresinizi giriniz"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefon</FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="Telefon numaranızı giriniz"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit" className="w-full">
              Başvuru Yap
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default BecomeMentor;
